import React, { useState, useRef } from 'react';
import { db, getCollectionName } from './firebase';
import { collection, addDoc, serverTimestamp } from './firestore-compat';

const TIPOS_PRONTUARIO = [
  { k: 'sintetico', l: '📊 Prontuário Sintético' },
  { k: 'analitico', l: '📋 Prontuário Analítico' },
];

export default function UploadProntuario() {
  const [tipo, setTipo] = useState('sintetico');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const [competencia, setCompetencia] = useState('');
  const [descricao, setDescricao] = useState('');
  const inputRef = useRef(null);

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return { headers: [], rows: [] };
    const sep = lines[0].includes(';') ? ';' : ',';
    const parseRow = l => l.split(sep).map(v => v.replace(/^"|"$/g, '').trim());
    const hs = parseRow(lines[0]);
    const rows = lines.slice(1).map(parseRow);
    return { headers: hs, rows };
  };

  // Muitos exports de sistemas legados (ex: planilhas exportadas no Windows) usam Windows-1252/ISO-8859-1
  // em vez de UTF-8. Ler forçando UTF-8 corrompe acentos (vira "�"). Aqui detectamos automaticamente:
  // tentamos decodificar como UTF-8 estrito primeiro; se falhar, caímos para Windows-1252.
  const decodeFileText = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = ev => {
      const buffer = ev.target.result;
      let text;
      try {
        text = new TextDecoder('utf-8', { fatal: true }).decode(buffer);
      } catch {
        text = new TextDecoder('windows-1252').decode(buffer);
      }
      resolve(text);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

  const handleFile = async e => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f); setDone(null);
    const text = await decodeFileText(f);
    const { headers: hs, rows } = parseCSV(text);
    setHeaders(hs);
    setPreview(rows.slice(0, 8));
  };

  const handleImport = async () => {
    if (!file) return;
    setSaving(true); setDone(null);
    try {
      const text = await decodeFileText(file);
      const { headers: hs, rows } = parseCSV(text);
      const coll = collection(db, getCollectionName('prontuario_uploads'));
      const rowObjs = rows.map(r => Object.fromEntries(hs.map((h, i) => [h, r[i] ?? ''])));
      await addDoc(coll, {
        tipo,
        competencia: competencia.trim(),
        descricao: descricao.trim(),
        nomeArquivo: file.name,
        totalLinhas: rowObjs.length,
        headers: hs,
        rows: rowObjs,
        criadoEm: serverTimestamp(),
      });
      setDone(rowObjs.length);
      setFile(null); setPreview([]); setHeaders([]);
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      console.error('Erro ao importar prontuário:', err);
      alert('Erro ao importar o arquivo: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 28, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ fontWeight: 800, color: '#1e293b', fontSize: '1.15rem', marginBottom: 6 }}>📄 Upload CSV — Prontuários</div>
      <div style={{ color: '#64748b', fontSize: '0.84rem', marginBottom: 24 }}>Importe o arquivo CSV com os dados de envio de prontuários para alimentar o dashboard.</div>

      <div style={{ background: '#fff', borderRadius: 14, padding: 28, border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 20 }}>
        <div style={{ marginBottom: 18 }}>
          <label style={{ color: '#64748b', fontSize: '0.72rem', display: 'block', marginBottom: 6 }}>TIPO DE RELATÓRIO</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {TIPOS_PRONTUARIO.map(t => (
              <button key={t.k} type="button" onClick={() => setTipo(t.k)}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem',
                  border: `1.5px solid ${tipo === t.k ? '#6366f1' : '#e2e8f0'}`,
                  background: tipo === t.k ? '#eef2ff' : '#fff',
                  color: tipo === t.k ? '#4338ca' : '#64748b',
                }}>
                {t.l}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label style={{ color: '#64748b', fontSize: '0.72rem', display: 'block', marginBottom: 4 }}>COMPETÊNCIA (ex: 05/2025)</label>
            <input value={competencia} onChange={e => setCompetencia(e.target.value)} placeholder="MM/AAAA"
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.87rem', boxSizing: 'border-box', outline: 'none' }} />
          </div>
          <div style={{ flex: 2, minWidth: 200 }}>
            <label style={{ color: '#64748b', fontSize: '0.72rem', display: 'block', marginBottom: 4 }}>DESCRIÇÃO (opcional)</label>
            <input value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Lote de prontuários — mês de referência"
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.87rem', boxSizing: 'border-box', outline: 'none' }} />
          </div>
        </div>

        <label style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          border: '2px dashed #e2e8f0', borderRadius: 12, padding: '36px 24px', cursor: 'pointer',
          background: file ? '#f0fdf4' : '#f8fafc', transition: 'all 0.2s', marginBottom: 16,
        }}
          onDragOver={e => { e.preventDefault(); }}
          onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { inputRef.current.files = e.dataTransfer.files; handleFile({ target: { files: [f] } }); } }}>
          <input ref={inputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFile} />
          <span style={{ fontSize: 36, marginBottom: 8 }}>{file ? '✅' : '📂'}</span>
          <span style={{ fontWeight: 700, color: file ? '#16a34a' : '#6366f1', fontSize: '0.9rem' }}>{file ? file.name : 'Clique ou arraste o arquivo CSV aqui'}</span>
          <span style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: 4 }}>Suporta separador vírgula (,) ou ponto-e-vírgula (;)</span>
        </label>

        {preview.length > 0 && (
          <div style={{ marginBottom: 16, overflowX: 'auto' }}>
            <div style={{ fontWeight: 700, color: '#374151', fontSize: '0.82rem', marginBottom: 8 }}>Pré-visualização (primeiras {preview.length} linhas):</div>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.75rem', minWidth: 400, width: '100%' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {headers.map(h => <th key={h} style={{ padding: '6px 10px', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#64748b', whiteSpace: 'nowrap' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {preview.map((row, ri) => (
                  <tr key={ri} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    {row.map((cell, ci) => <td key={ci} style={{ padding: '5px 10px', color: '#374151', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ color: '#94a3b8', fontSize: '0.71rem', marginTop: 4 }}>{headers.length} colunas detectadas</div>
          </div>
        )}

        {done !== null && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px', marginBottom: 14, color: '#16a34a', fontWeight: 700, fontSize: '0.88rem' }}>
            ✅ {done} registros importados com sucesso!
          </div>
        )}

        <button onClick={handleImport} disabled={!file || saving}
          style={{ width: '100%', padding: 14, borderRadius: 10, border: 'none', background: file ? '#6366f1' : '#e2e8f0', color: file ? '#fff' : '#94a3b8', fontWeight: 800, fontSize: '0.95rem', cursor: file ? 'pointer' : 'default', transition: 'all 0.2s' }}>
          {saving ? '⏳ Importando...' : '📤 Importar CSV'}
        </button>
      </div>
    </div>
  );
}
